import {Component, computed, effect, viewChild, signal, ChangeDetectionStrategy} from "@angular/core"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, SelectionPossibilities} from "@app/template-editor/components/value-slot/value-slot.component"
import {
    NumberLike,
    GeometryAttributeNode,
    GeometryPackOperatorNode,
    GeometryConstOperatorNode,
    GeometryCastOperatorNode,
    GeometryLabelPrimitivesOperatorNode,
    GeometryWeldOperatorNode,
    GeometryGetAttributeNode,
    GeometryGetAttributeComponentNode,
    GeometryMathOperatorNode,
    GeometryUnaryMathOperatorNode,
    GeometryBinaryMathOperatorNode,
    GeometryPrimitiveNode,
    IndexingDim,
    componentOptions,
    ComponentOption,
    MathOperatorOption,
    mathOperatorOption,
    mathOperatorOptions,
    isMathOperatorOption,
    createPointGeometryUnaryOrBinaryMathOperator,
    createCurveGeometryUnaryOrBinaryMathOperator,
    createMeshGeometryUnaryOrBinaryMathOperator,
    PointGeometryMathOperator,
    CurveGeometryMathOperator,
    MeshGeometryMathOperator,
    CurveGeometryFlattenOperator,
    ConstOperatorTypeOption,
    constOperatorTypeOptions,
    CastOperatorTypeOption,
    castOperatorTypeOptions,
    CurveGeometryDiffOperator,
    pointGeometryPrimitiveOptions,
    PointGeometryPrimitiveOption,
    curveGeometryPrimitiveOptions,
    CurveGeometryPrimitiveOption,
    meshGeometryPrimitiveOptions,
    MeshGeometryPrimitiveOption,
    pointGeometryPrimitiveOption,
    curveGeometryPrimitiveOption,
    meshGeometryPrimitiveOption,
    optionForPointGeometryPrimitive,
    optionForCurveGeometryPrimitive,
    optionForMeshGeometryPrimitive,
    isPointGeometryPrimitiveLike,
    isCurveGeometryPrimitiveLike,
    isMeshGeometryPrimitiveLike,
    createPointGeometryPrimitive,
    createCurveGeometryPrimitive,
    createMeshGeometryPrimitive,
    PointGeometryPrimitive,
    CurveGeometryPrimitive,
    MeshGeometryPrimitive,
    PointGeometryLinePoints2,
    PointGeometryLinePoints3,
    CurveGeometryLine1,
    CurveGeometryLine2,
    CurveGeometryLine3,
    PointGeometryPointSet1,
    PointGeometryPointSet2,
    PointGeometryPointSet3,
    MeshGeometryGrid2,
    isPointGeometryPrimitiveLinePointsLike,
    isPointGeometryPrimitivePointSetLike,
    MeshGeometryNormalsOperator,
} from "@cm/template-nodes"
import type {PointGeometryAttributeLikeNode, CurveGeometryAttributeLikeNode, MeshGeometryAttributeLikeNode} from "@cm/template-nodes"
import {createSelectorDummyNode} from "@template-editor/components/inspectors/geometry-graph-node-inspector/components/helpers"
import {z} from "zod/v4-mini"

class MathOperatorSelectionDummyNode extends createSelectorDummyNode<{operator: MathOperatorOption}>(
    z.object({operator: mathOperatorOption}),
    "MathOperatorSelectionDummyNode",
) {}

class PointGeometryPrimitiveSelectionDummyNode extends createSelectorDummyNode<{primitive: PointGeometryPrimitiveOption}>(
    z.object({primitive: pointGeometryPrimitiveOption}),
    "PointGeometryPrimitiveSelectionDummyNode",
) {}

class CurveGeometryPrimitiveSelectionDummyNode extends createSelectorDummyNode<{primitive: CurveGeometryPrimitiveOption}>(
    z.object({primitive: curveGeometryPrimitiveOption}),
    "CurveGeometryPrimitiveSelectionDummyNode",
) {}

class MeshGeometryPrimitiveSelectionDummyNode extends createSelectorDummyNode<{primitive: MeshGeometryPrimitiveOption}>(
    z.object({primitive: meshGeometryPrimitiveOption}),
    "MeshGeometryPrimitiveSelectionDummyNode",
) {}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-geometry-attribute-like-node-inspector",
    templateUrl: "./geometry-attribute-like-node-inspector.component.html",
    styleUrls: ["./geometry-attribute-like-node-inspector.component.scss"],
    imports: [InspectorSectionComponent, ValueSlotComponent, ButtonComponent],
    standalone: true,
})
export class GeometryAttributeLikeNodeInspectorComponent extends BaseInspectorComponent<GeometryAttributeNode<IndexingDim>> {
    readonly $mathOperatorLikeOperatorParameter = viewChild<ValueSlotComponent<MathOperatorSelectionDummyNode>>("mathOperatorParameter")
    readonly $pointGeometryPrimitiveLikePrimitiveParameter =
        viewChild<ValueSlotComponent<PointGeometryPrimitiveSelectionDummyNode>>("pointGeometryPrimitiveParameter")
    readonly $curveGeometryPrimitiveLikePrimitiveParameter =
        viewChild<ValueSlotComponent<CurveGeometryPrimitiveSelectionDummyNode>>("curveGeometryPrimitiveParameter")
    readonly $meshGeometryPrimitiveLikePrimitiveParameter =
        viewChild<ValueSlotComponent<MeshGeometryPrimitiveSelectionDummyNode>>("meshGeometryPrimitiveParameter")

    readonly $getGeometryAttributeLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryGetAttributeNode ? node : null
    })
    readonly $getGeometryAttributeComponentLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryGetAttributeComponentNode ? node : null
    })
    readonly $packOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryPackOperatorNode ? node : null
    })
    readonly $mathOperatorLike = computed(() => {
        const node = this.$node()
        if (!(node instanceof GeometryMathOperatorNode)) return null
        this.$parameters()
        // The operator child is always Unary|Binary at runtime — narrow to expose `parameters.operator`.
        const op = node.parameters.operator
        if (op instanceof GeometryUnaryMathOperatorNode || op instanceof GeometryBinaryMathOperatorNode) return op
        return null
    })
    readonly $unaryMathOperatorLike = computed(() => {
        const node = this.$mathOperatorLike()
        if (!node) return null
        return node instanceof GeometryUnaryMathOperatorNode ? node : null
    })
    readonly $binaryMathOperatorLike = computed(() => {
        const node = this.$mathOperatorLike()
        if (!node) return null
        return node instanceof GeometryBinaryMathOperatorNode ? node : null
    })
    readonly $flattenOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof CurveGeometryFlattenOperator ? node : null
    })
    readonly $constOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryConstOperatorNode ? node : null
    })
    readonly $castOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryCastOperatorNode ? node : null
    })
    readonly $diffOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof CurveGeometryDiffOperator ? node : null
    })
    readonly $primitiveLike = computed(() => {
        const node = this.$node()
        if (!node) return null
        this.$parameters()
        return node instanceof GeometryPrimitiveNode ? node.parameters.primitive : null
    })
    readonly $pointGeometryPrimitiveLike = computed(() => {
        const node = this.$primitiveLike()
        if (!node) return null
        return isPointGeometryPrimitiveLike(node) ? node : null
    })
    readonly $pointGeometryPrimitivePointSetLike = computed(() => {
        const node = this.$pointGeometryPrimitiveLike()
        if (!node || !isPointGeometryPrimitivePointSetLike(node)) return null
        return {
            pointSet1: node instanceof PointGeometryPointSet1 ? node : null,
            pointSet2: node instanceof PointGeometryPointSet2 ? node : null,
            pointSet3: node instanceof PointGeometryPointSet3 ? node : null,
        }
    })
    readonly $pointGeometryPrimitiveLinePointsLike = computed(() => {
        const node = this.$pointGeometryPrimitiveLike()
        if (!node || !isPointGeometryPrimitiveLinePointsLike(node)) return null
        return node
    })
    readonly $curveGeometryPrimitiveLike = computed(() => {
        const node = this.$primitiveLike()
        if (!node) return null
        return isCurveGeometryPrimitiveLike(node) ? node : null
    })
    readonly $curveGeometryPrimitiveLineLike = computed(() => {
        const node = this.$curveGeometryPrimitiveLike()
        if (
            !node ||
            !(
                isCurveGeometryPrimitiveLike(node) &&
                (node instanceof CurveGeometryLine1 || node instanceof CurveGeometryLine2 || node instanceof CurveGeometryLine3)
            )
        )
            return null
        return node
    })
    readonly $primitiveLikeWithX0X1Parameters = computed(() => {
        const node = this.$pointGeometryPrimitiveLinePointsLike() ?? this.$curveGeometryPrimitiveLineLike()
        if (!node) return null
        return node
    })
    readonly $primitiveLikeWithY0Y1Parameters = computed(() => {
        const linePointsLike = this.$pointGeometryPrimitiveLinePointsLike()
        const curveLineLike = this.$curveGeometryPrimitiveLineLike()
        if (linePointsLike && (linePointsLike instanceof PointGeometryLinePoints2 || linePointsLike instanceof PointGeometryLinePoints3)) return linePointsLike
        if (curveLineLike && (curveLineLike instanceof CurveGeometryLine2 || curveLineLike instanceof CurveGeometryLine3)) return curveLineLike
        return null
    })
    readonly $primitiveLikeWithZ0Z1Parameters = computed(() => {
        const linePointsLike = this.$pointGeometryPrimitiveLinePointsLike()
        const curveLineLike = this.$curveGeometryPrimitiveLineLike()
        if (linePointsLike && linePointsLike instanceof PointGeometryLinePoints3) return linePointsLike
        if (curveLineLike && curveLineLike instanceof CurveGeometryLine3) return curveLineLike
        return null
    })

    readonly $meshGeometryPrimitiveLike = computed(() => {
        const node = this.$primitiveLike()
        if (!node) return null
        return isMeshGeometryPrimitiveLike(node) ? node : null
    })

    readonly $meshGeometryPrimitiveGrid2Like = computed(() => {
        const node = this.$meshGeometryPrimitiveLike()
        if (!node) return null
        return node instanceof MeshGeometryGrid2 ? node : null
    })

    readonly $labelPrimitivesOperatorLike = computed(() => {
        const node = this.$node()
        if (!node) return null
        return node instanceof GeometryLabelPrimitivesOperatorNode ? node : null
    })

    readonly $weldOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryWeldOperatorNode ? node : null
    })

    readonly $normalsOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof MeshGeometryNormalsOperator ? node : null
    })

    readonly $mathOperatorSelectionDummyNode = signal(new MathOperatorSelectionDummyNode({parameters: {operator: "add"}}))
    readonly $pointGeometryPrimitiveSelectionDummyNode = signal(new PointGeometryPrimitiveSelectionDummyNode({parameters: {primitive: "linePoints1"}}))
    readonly $curveGeometryPrimitiveSelectionDummyNode = signal(new CurveGeometryPrimitiveSelectionDummyNode({parameters: {primitive: "line1"}}))
    readonly $meshGeometryPrimitiveSelectionDummyNode = signal(new MeshGeometryPrimitiveSelectionDummyNode({parameters: {primitive: "grid2"}}))

    constructor() {
        super()

        effect(() => {
            const mathOperatorLike = this.$mathOperatorLike()
            if (!mathOperatorLike) return
            const operatorOption = mathOperatorLike.parameters.operator
            this.$mathOperatorSelectionDummyNode().updateParametersExplicit({operator: operatorOption})
            this.$mathOperatorLikeOperatorParameter()?.forceTriggerRecompute()
        })

        effect(() => {
            const pointPrimitiveLike = this.$pointGeometryPrimitiveLike()
            if (!pointPrimitiveLike) return
            const primitiveOption = optionForPointGeometryPrimitive(pointPrimitiveLike)
            this.$pointGeometryPrimitiveSelectionDummyNode().updateParametersExplicit({primitive: primitiveOption})
            this.$pointGeometryPrimitiveLikePrimitiveParameter()?.forceTriggerRecompute()
        })

        effect(() => {
            const curvePrimitiveLike = this.$curveGeometryPrimitiveLike()
            if (!curvePrimitiveLike) return
            const primitiveOption = optionForCurveGeometryPrimitive(curvePrimitiveLike)
            this.$curveGeometryPrimitiveSelectionDummyNode().updateParametersExplicit({primitive: primitiveOption})
            this.$curveGeometryPrimitiveLikePrimitiveParameter()?.forceTriggerRecompute()
        })

        effect(() => {
            const meshPrimitiveLike = this.$meshGeometryPrimitiveLike()
            if (!meshPrimitiveLike) return
            const primitiveOption = optionForMeshGeometryPrimitive(meshPrimitiveLike)
            this.$meshGeometryPrimitiveSelectionDummyNode().updateParametersExplicit({primitive: primitiveOption})
            this.$meshGeometryPrimitiveLikePrimitiveParameter()?.forceTriggerRecompute()
        })
    }

    onMathOperatorSelectionChange(newOperatorOption: unknown) {
        if (typeof newOperatorOption !== "string" || !isMathOperatorOption(newOperatorOption)) return

        const operator = this.$node()
        if (!operator) return

        type ArgForOperator<T extends PointGeometryMathOperator | CurveGeometryMathOperator | MeshGeometryMathOperator> = T extends PointGeometryMathOperator
            ? PointGeometryAttributeLikeNode
            : T extends CurveGeometryMathOperator
              ? CurveGeometryAttributeLikeNode
              : T extends MeshGeometryMathOperator
                ? MeshGeometryAttributeLikeNode
                : never

        const getArgs = <T extends PointGeometryMathOperator | CurveGeometryMathOperator | MeshGeometryMathOperator>(
            operator: T,
        ): [ArgForOperator<T>, ArgForOperator<T> | NumberLike | null] => {
            const operatorParameter = operator.parameters.operator

            const arg1 = operatorParameter instanceof GeometryUnaryMathOperatorNode ? operatorParameter.parameters.arg : operatorParameter.parameters.arg1
            const arg2 = operatorParameter instanceof GeometryBinaryMathOperatorNode ? operatorParameter.parameters.arg2 : null

            return [arg1 as ArgForOperator<T>, arg2 as ArgForOperator<T> | NumberLike | null]
        }

        if (operator instanceof PointGeometryMathOperator)
            operator.updateParameters({operator: createPointGeometryUnaryOrBinaryMathOperator(newOperatorOption, ...getArgs(operator))})
        else if (operator instanceof CurveGeometryMathOperator)
            operator.updateParameters({operator: createCurveGeometryUnaryOrBinaryMathOperator(newOperatorOption, ...getArgs(operator))})
        else if (operator instanceof MeshGeometryMathOperator)
            operator.updateParameters({operator: createMeshGeometryUnaryOrBinaryMathOperator(newOperatorOption, ...getArgs(operator))})
        else throw new Error(`Invalid geometry type`)
    }

    onPointGeometryPrimitiveSelectionChange(newPrimitiveOption: unknown) {
        const parsedNewPrimitiveOption = pointGeometryPrimitiveOption.safeParse(newPrimitiveOption)
        if (!parsedNewPrimitiveOption.success) return

        const primitiveLike = this.$node()
        if (!(primitiveLike instanceof PointGeometryPrimitive)) return

        const isPointSetOption = parsedNewPrimitiveOption.data.startsWith("pointSet")

        primitiveLike.updateParameters({
            primitive: createPointGeometryPrimitive(
                parsedNewPrimitiveOption.data,
                isPointSetOption ? {data: []} : {numPoints: 1, x0: null, x1: null, y0: null, y1: null, z0: null, z1: null},
            ),
        })
    }

    onCurveGeometryPrimitiveSelectionChange(newPrimitiveOption: unknown) {
        const parsedNewPrimitiveOption = curveGeometryPrimitiveOption.safeParse(newPrimitiveOption)
        if (!parsedNewPrimitiveOption.success) return

        const primitiveLike = this.$node()
        if (!(primitiveLike instanceof CurveGeometryPrimitive)) return

        primitiveLike.updateParameters({
            primitive: createCurveGeometryPrimitive(parsedNewPrimitiveOption.data, null, {x0: null, x1: null, y0: null, y1: null, z0: null, z1: null}),
        })
    }

    onMeshGeometryPrimitiveSelectionChange(newPrimitiveOption: unknown) {
        const parsedNewPrimitiveOption = meshGeometryPrimitiveOption.safeParse(newPrimitiveOption)
        if (!parsedNewPrimitiveOption.success) return

        const primitiveLike = this.$node()
        if (!(primitiveLike instanceof MeshGeometryPrimitive)) return

        primitiveLike.updateParameters({
            primitive: createMeshGeometryPrimitive("grid2", {
                originX: null,
                originY: null,
                basis1X: null,
                basis1Y: null,
                basis2X: null,
                basis2Y: null,
                numXSegments: null,
                numYSegments: null,
            }),
        })
    }

    addEntryForPointGeometryPrimitivePointSetLike<T extends PointGeometryPointSet2 | PointGeometryPointSet3>(node: T) {
        this.sceneManagerService.modifyTemplateGraph(() => {
            if (node instanceof PointGeometryPointSet2) {
                node.updateParameters({data: [...node.parameters.data, {x: null, y: null}]})
            } else if (node instanceof PointGeometryPointSet3) {
                node.updateParameters({data: [...node.parameters.data, {x: null, y: null, z: null}]})
            } else throw new Error(`Invalid point geometry primitive point set type`)
        })
    }

    removeEntryForPointGeometryPrimitivePointSetLike<T extends PointGeometryPointSet2 | PointGeometryPointSet3>(node: T, index: number) {
        this.sceneManagerService.modifyTemplateGraph(() => {
            if (node instanceof PointGeometryPointSet2) {
                node.updateParameters({data: node.parameters.data.filter((_, i) => i !== index)})
            } else if (node instanceof PointGeometryPointSet3) {
                node.updateParameters({data: node.parameters.data.filter((_, i) => i !== index)})
            } else throw new Error(`Invalid point geometry primitive point set type`)
        })
    }

    componentOptionsSelectionPossibilities: SelectionPossibilities<ComponentOption> = componentOptions.map((option) => ({
        name: option,
        value: option,
    }))
    mathOperatorSelectionPossibilities: SelectionPossibilities<MathOperatorOption> = mathOperatorOptions.map((operator) => ({
        name: operator,
        value: operator,
    }))
    constOperatorTypeSelectionPossibilities: SelectionPossibilities<ConstOperatorTypeOption> = constOperatorTypeOptions.map((option) => ({
        name: option,
        value: option,
    }))
    castOperatorTypeSelectionPossibilities: SelectionPossibilities<CastOperatorTypeOption> = castOperatorTypeOptions.map((option) => ({
        name: option,
        value: option,
    }))
    pointGeometryPrimitiveSelectionPossibilities: SelectionPossibilities<PointGeometryPrimitiveOption> = pointGeometryPrimitiveOptions.map((option) => ({
        name: option,
        value: option,
    }))
    curveGeometryPrimitiveSelectionPossibilities: SelectionPossibilities<CurveGeometryPrimitiveOption> = curveGeometryPrimitiveOptions.map((option) => ({
        name: option,
        value: option,
    }))
    meshGeometryPrimitiveSelectionPossibilities: SelectionPossibilities<MeshGeometryPrimitiveOption> = meshGeometryPrimitiveOptions.map((option) => ({
        name: option,
        value: option,
    }))
}
